import { UnknownAction } from 'redux';

import globalLogger from '../logger';
import { MiddlewareNextFunction, ReduxState, ReduxStore } from '../types/reduxTypes';
import { DRAG_END, DRAG_START } from '../reducers/draggingConstants';
import { LINE_POINT_DRAG_START } from '../element/line/lineUiConstants';
import {
    ELEMENT_CREATE,
    ELEMENT_CREATION_SOURCES,
    ELEMENT_MOVE_MULTI,
    ELEMENT_MOVE_OPERATIONS,
    ELEMENT_SET_TYPE,
    ELEMENT_UPDATE,
    ELEMENT_UPDATE_TYPE,
} from '../../common/elements/elementConstants';
import {
    ElementCreateAction,
    isActionElementMoveMulti,
    isActionElementUpdate,
} from '../../common/elements/elementActionTypes';
import {
    ELEMENTS_DESELECT_ALL,
    ELEMENTS_DESELECTED,
    ELEMENTS_SELECTED,
} from '../../common/elements/selectionConstants';
import { CommentActionType } from '../../common/comments/commentConstants';
import { POPUP_OPEN, POPUP_TOGGLE } from '../components/popupPanel/popupConstants';
import { PopupToggleAction } from '../components/popupPanel/popupActionTypes';
import { BATCH_ACTION_TYPE } from '../../common/store/reduxBulkingMiddleware';
import { BoardSections } from '../../common/boards/boardConstants';
import { ElementType } from '../../common/elements/elementTypes';
import { getElements } from '../element/selectors/elementsSelector';
import { getUserIdFromAction } from '../../common/actionUtils';
import { ActionObject } from '../../common/actions/actionTypes';
import { MNElementLocation } from '../../common/elements/elementModelTypes';
import { ImmutableMap } from '../../common/utils/immutableHelper';
import { getRemoteSelectedElementDetails } from '../remoteActivity/remoteSelectedElementsSelector';
import { activePopupSelector } from '../components/popupPanel/popupOpenSelector';
import { getElement } from '../../common/elements/utils/elementTraversalUtils';
import { isBoardLike, isColumn, isLink, isTask } from '../../common/elements/utils/elementTypeUtils';
import { getPhysicalId } from '../../common/elements/utils/elementPropertyUtils';
import { getCurrentBoardId } from '../reducers/currentBoardId/currentBoardIdSelector';
import { getCurrentUserQuickNotesRootBoardId } from '../user/currentUserSelector';
import { getIsSoundsEnabledForCurrentUser } from './soundEnabledSelector';
import { playSound } from './soundService';
import { SoundId } from './soundConstants';

const logger = globalLogger.createChannel('sounds');

/** Fields the socket / bulking layers can add to any action */
type ActionMeta = { remote?: boolean; batched?: boolean };

// Local shapes for actions whose creators don't export a type
type DragStartAction = UnknownAction & { ids?: string[]; elementType?: string };
type DragEndAction = UnknownAction & {
    dropState?: { droppedElementIds?: string[] };
    dropLocation?: MNElementLocation;
};
type LinePointDragStartAction = UnknownAction & { isControlPoint?: boolean };
type ElementSetTypeAction = UnknownAction & { id?: string };
type SelectionAction = UnknownAction & { ids?: string[] };
type CommentUpdateAction = UnknownAction & { contentUpdate?: { updateType?: string } };
type BatchAction = UnknownAction & { payload?: UnknownAction[] };

type SoundRuleContext = {
    action: UnknownAction & ActionMeta;
    /** Store state captured before the action was reduced */
    prevState: ReduxState;
};

type SoundRule = (context: SoundRuleContext) => SoundId | SoundId[] | undefined;

const isRemote = (action: UnknownAction & ActionMeta) => !!action.remote;

const getElementsById = (state: ReduxState, ids: string[] | undefined) => {
    if (!ids?.length) return [];
    const elements = getElements(state);
    return ids.map((id) => getElement(elements, id)).filter(Boolean);
};

// ---------------------------------------------------------------------------
// Element pickup / drop
// ---------------------------------------------------------------------------

// Every element type shares the card pickup / drop sounds. Existing elements arrive as ids;
// toolbar creation-tool drags carry an `elementType` but no ids yet — either way, something is
// being dragged, so it sounds.
const dragStartRule: SoundRule = ({ action }) => {
    const { ids, elementType } = action as DragStartAction;
    return ids?.length || elementType ? SoundId.CardPickup : undefined;
};

// Grabbing a line endpoint counts as a pickup too; dragging the curve's control point stays silent.
// (The matching drop is component-driven — see ConnectedEdgeDragHandle.jsx — because only the drop
// target knows whether the edge connected to an element.)
const linePointDragStartRule: SoundRule = ({ action }) =>
    (action as LinePointDragStartAction).isControlPoint ? undefined : SoundId.CardPickup;

/**
 * Boards whose contents are on screen: the open board (its canvas and unsorted panel — resolved
 * to its physical id in case it's viewed through an alias) and quick notes. Drops into these
 * are regular drops, not drops "into a board".
 */
const getOnScreenContainerBoardIds = (prevState: ReduxState): (string | undefined)[] => {
    const currentBoardId = getCurrentBoardId(prevState);
    const currentBoardPhysicalId = getPhysicalId(getElement(getElements(prevState), currentBoardId));
    return [currentBoardId, currentBoardPhysicalId, getCurrentUserQuickNotesRootBoardId(prevState)];
};

// Cancelled drags dispatch dragEnd() with no dropState — only successful drops make a sound.
// Trash drops stay quiet here (the resulting move plays the delete sound), and drops into a
// board that isn't on screen (a board icon, a breadcrumb) get the board drop sound
const dragEndRule: SoundRule = ({ action, prevState }) => {
    const { dropState, dropLocation } = action as DragEndAction;
    if (!dropState?.droppedElementIds?.length) return undefined;

    if (dropLocation?.section === BoardSections.TRASH) return undefined;

    const dropParent = getElement(getElements(prevState), dropLocation?.parentId);
    const isDropIntoBoard =
        isBoardLike(dropParent) && !getOnScreenContainerBoardIds(prevState).includes(dropLocation?.parentId);
    return isDropIntoBoard ? SoundId.BoardDrop : SoundId.CardDrop;
};

// ---------------------------------------------------------------------------
// Element updates: column collapse, legacy task completion, reactions
// ---------------------------------------------------------------------------

const elementUpdateRule: SoundRule = ({ action, prevState }) => {
    if (!isActionElementUpdate(action) || isRemote(action)) return undefined;

    if (action.updateType === ELEMENT_UPDATE_TYPE.REACTION) return SoundId.ReactionAdd;

    // Legacy (element-per-task) task lists
    if (action.updateType === ELEMENT_UPDATE_TYPE.TOGGLE_COMPLETION) {
        const update = action.updates[0];
        if (!update) return undefined;
        return update.changes?.isComplete ? SoundId.CheckboxTick : SoundId.CheckboxUntick;
    }

    // Column collapse / expand
    const elements = getElements(prevState);
    const collapseUpdate = action.updates.find(
        (update) => update.changes?.collapsed !== undefined && isColumn(getElement(elements, update.id)),
    );
    if (collapseUpdate) return collapseUpdate.changes.collapsed ? SoundId.Contract : SoundId.Expand;

    return undefined;
};

/**
 * Remember what was just created, so e.g. the first task that comes with a brand-new task list
 * doesn't sound like a task being added.
 */
const RECENTLY_CREATED_WINDOW_MS = 1000;
const recentlyCreatedElementIds = new Map<string, number>();

const rememberCreatedElement = (id: string) => {
    const now = Date.now();
    recentlyCreatedElementIds.set(id, now);
    recentlyCreatedElementIds.forEach((createdAt, createdId) => {
        if (now - createdAt > RECENTLY_CREATED_WINDOW_MS) recentlyCreatedElementIds.delete(createdId);
    });
};

const wasRecentlyCreated = (id: string | undefined) => {
    if (!id) return false;
    const createdAt = recentlyCreatedElementIds.get(id);
    return createdAt !== undefined && Date.now() - createdAt <= RECENTLY_CREATED_WINDOW_MS;
};

const IN_PLACE_CREATION_SOURCES = new Set<string>([
    ELEMENT_CREATION_SOURCES.DOUBLE_CLICK,
    ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
]);

const elementCreateRule: SoundRule = ({ action }) => {
    if (isRemote(action)) return undefined;

    const createAction = action as ElementCreateAction;
    if (createAction.id) rememberCreatedElement(createAction.id);

    // Legacy task lists: the first task that comes with a freshly created list is part of creating
    // the list (which has already sounded), not a task being added — stay quiet
    if (createAction.elementType === ElementType.TASK_TYPE && wasRecentlyCreated(createAction.location?.parentId)) {
        return undefined;
    }

    // Creating an element in place — double-clicking the canvas or picking one from the context menu —
    // sounds like a drop
    if (createAction.creationSource && IN_PLACE_CREATION_SOURCES.has(createAction.creationSource)) {
        return SoundId.CardDrop;
    }

    // Legacy task lists: a new task element appearing
    return createAction.elementType === ElementType.TASK_TYPE ? SoundId.TaskNewTaskAppear : undefined;
};

// A link card resolving its URL (fetching title / preview, possibly becoming an image or board
// alias) lands as ELEMENT_SET_TYPE on an element that was a link — it "expands" into its content
const elementSetTypeRule: SoundRule = ({ action, prevState }) => {
    if (isRemote(action)) return undefined;
    const previousElement = getElement(getElements(prevState), (action as ElementSetTypeAction).id);
    return isLink(previousElement) ? SoundId.Expand : undefined;
};

// ---------------------------------------------------------------------------
// Delete (move to trash)
// ---------------------------------------------------------------------------

const elementMoveMultiRule: SoundRule = ({ action, prevState }) => {
    if (!isActionElementMoveMulti(action) || isRemote(action)) return undefined;

    const isTrash =
        action.moveOperation === ELEMENT_MOVE_OPERATIONS.TRASH ||
        action.moves.some((move) => move.location?.section === BoardSections.TRASH);
    if (!isTrash) return undefined;

    // Deleting individual task items (legacy task lists) isn't "deleting an element" — stay quiet
    // unless at least one whole (non-task) element is going to the trash
    const movedIds = action.moves.map((move) => move.id);
    const movedElements = getElementsById(prevState, movedIds);
    if (movedElements.length && movedElements.every(isTask)) return undefined;

    return SoundId.DeleteSomething;
};

// ---------------------------------------------------------------------------
// Selection (remote users only — local select / deselect are silent)
// ---------------------------------------------------------------------------

const elementsSelectedRule: SoundRule = ({ action }) =>
    isRemote(action) && (action as SelectionAction).ids?.length ? SoundId.RemoteUserSelect : undefined;

/** Whether any of the given elements were visibly selected by the acting remote user before this action. */
const hadVisibleRemoteSelection = (prevState: ReduxState, ids: string[], userId: string | undefined): boolean => {
    const remoteSelectedElements = getRemoteSelectedElementDetails(prevState);
    return ids.some((id) => {
        // Deselected entries linger as {userId: null} (see selectedElementReducer), so check the
        // userId rather than entry existence
        const selectedByUserId = remoteSelectedElements?.getIn([id, 'userId']);
        return !!selectedByUserId && (!userId || selectedByUserId === userId);
    });
};

/** Whether the given remote user had any elements visibly selected before this action. */
const userHadVisibleRemoteSelection = (prevState: ReduxState, userId: string | undefined): boolean => {
    if (!userId) return false;
    const remoteSelectedElements = getRemoteSelectedElementDetails(prevState);
    return !!remoteSelectedElements?.some((entry: ImmutableMap) => entry?.get('userId') === userId);
};

// Deselect sounds only play for selections we could actually see (tracked in remoteActivity) —
// remote selection actions can arrive for boards / selections we have no visibility of
const elementsDeselectedRule: SoundRule = ({ action, prevState }) => {
    if (!isRemote(action)) return undefined;
    const { ids } = action as SelectionAction;
    if (!ids?.length) return undefined;
    const userId = getUserIdFromAction(action as ActionObject);
    return hadVisibleRemoteSelection(prevState, ids, userId) ? SoundId.RemoteUserDeselect : undefined;
};

// The client's deselectAll carries ids, but the server broadcasts an id-less deselect-all on every
// socket disconnect — whether or not the user had a selection — so those must stay silent unless
// the user visibly had something selected
const elementsDeselectAllRule: SoundRule = ({ action, prevState }) => {
    if (!isRemote(action)) return undefined;
    const { ids } = action as SelectionAction;
    const userId = getUserIdFromAction(action as ActionObject);
    const hadSelection = ids?.length
        ? hadVisibleRemoteSelection(prevState, ids, userId)
        : userHadVisibleRemoteSelection(prevState, userId);
    return hadSelection ? SoundId.RemoteUserDeselect : undefined;
};

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

// A comment being added — by the local user or a collaborator alike
const commentsAddRule: SoundRule = ({ action }) => {
    // Bulk-loaded comments (e.g. expanding a collapsed thread) are marked batched
    if (action.batched && !isRemote(action)) return undefined;
    return SoundId.CommentAdd;
};

// Starting a new (empty) comment in a thread — reuses the "new task appears" sound
const commentsStartEditingRule: SoundRule = () => SoundId.TaskNewTaskAppear;

const commentsUpdateRule: SoundRule = ({ action }) => {
    if (isRemote(action)) return undefined;
    const { contentUpdate } = action as CommentUpdateAction;
    return contentUpdate?.updateType === ELEMENT_UPDATE_TYPE.REACTION ? SoundId.ReactionAdd : undefined;
};

// ---------------------------------------------------------------------------
// Popups
// ---------------------------------------------------------------------------

const popupOpenRule: SoundRule = () => SoundId.PopupOpen;

// A toggle only sounds when it opens the popup (closing is silent)
const popupToggleRule: SoundRule = ({ action, prevState }) =>
    activePopupSelector(prevState)?.has((action as Partial<PopupToggleAction>).popupId) ? undefined : SoundId.PopupOpen;

// ---------------------------------------------------------------------------
// Rule table
// ---------------------------------------------------------------------------

const SOUND_RULES: Record<string, SoundRule> = {
    [DRAG_START]: dragStartRule,
    [DRAG_END]: dragEndRule,
    [LINE_POINT_DRAG_START]: linePointDragStartRule,
    [ELEMENT_UPDATE]: elementUpdateRule,
    [ELEMENT_CREATE]: elementCreateRule,
    [ELEMENT_SET_TYPE]: elementSetTypeRule,
    [ELEMENT_MOVE_MULTI]: elementMoveMultiRule,
    [ELEMENTS_SELECTED]: elementsSelectedRule,
    [ELEMENTS_DESELECTED]: elementsDeselectedRule,
    [ELEMENTS_DESELECT_ALL]: elementsDeselectAllRule,
    [CommentActionType.COMMENTS_ADD]: commentsAddRule,
    [CommentActionType.COMMENTS_UPDATE]: commentsUpdateRule,
    [CommentActionType.COMMENTS_START_EDITING]: commentsStartEditingRule,
    [POPUP_OPEN]: popupOpenRule,
    [POPUP_TOGGLE]: popupToggleRule,
};

/** Collects the sounds an action (or each action inside a batch) should trigger. */
const getSoundsForAction = (context: SoundRuleContext): SoundId[] => {
    const { action } = context;

    if (action.type === BATCH_ACTION_TYPE) {
        const payload = (action as BatchAction).payload || [];
        return payload.flatMap((batchedAction) => getSoundsForAction({ ...context, action: batchedAction }));
    }

    const rule = SOUND_RULES[action.type];
    if (!rule) return [];

    try {
        const result = rule(context);
        if (!result) return [];
        return Array.isArray(result) ? result : [result];
    } catch (error) {
        logger.warn(`Sound rule failed for action: ${action.type}`, error);
        return [];
    }
};

// ---------------------------------------------------------------------------
// Playback
// ---------------------------------------------------------------------------

/**
 * A remote selection *switch* arrives as a deselect action immediately followed by a select
 * (two separate socket actions). Hold the deselect sound briefly so a following select can
 * swallow it — observers should hear one sound for "selection moved", not deselect + select.
 */
const REMOTE_DESELECT_SOUND_GRACE_MS = 150;
let pendingRemoteDeselectTimeout: ReturnType<typeof setTimeout> | undefined;

const cancelPendingRemoteDeselectSound = () => {
    clearTimeout(pendingRemoteDeselectTimeout);
    pendingRemoteDeselectTimeout = undefined;
};

const scheduleRemoteDeselectSound = () => {
    cancelPendingRemoteDeselectSound();
    pendingRemoteDeselectTimeout = setTimeout(() => {
        pendingRemoteDeselectTimeout = undefined;
        playSound(SoundId.RemoteUserDeselect);
    }, REMOTE_DESELECT_SOUND_GRACE_MS);
};

const playSounds = (soundIds: SoundId[]) => {
    // De-dupe so e.g. a batch of 20 remote selections plays once
    const sounds = new Set(soundIds);

    if (sounds.has(SoundId.RemoteUserSelect)) {
        cancelPendingRemoteDeselectSound();
        sounds.delete(SoundId.RemoteUserDeselect);
    }

    sounds.forEach((soundId) =>
        soundId === SoundId.RemoteUserDeselect ? scheduleRemoteDeselectSound() : playSound(soundId),
    );
};

/**
 * Plays UX sounds in response to redux actions. Central hook point for action-driven sounds
 * so individual components don't need to know about audio. Component-driven sounds
 * (hover, toggles, drawing…) use usePlaySound instead.
 */
export default (store: ReduxStore) => (next: MiddlewareNextFunction) => (action: UnknownAction) => {
    const isRelevant = action.type === BATCH_ACTION_TYPE || !!SOUND_RULES[action.type];
    if (!isRelevant) return next(action);

    const prevState = store.getState();
    const result = next(action);

    if (!getIsSoundsEnabledForCurrentUser(prevState)) return result;

    playSounds(getSoundsForAction({ action, prevState }));

    return result;
};
